import { backgroundClient } from "@whatslly/commons/background/client";
import { DLPPolicy, DLPAction, DLPPolicyCondition } from "@whatslly/types/dlp/DLPPolicy";
import { Direction as DLPDirection, Operator as DLPOperator, OutboundMessageType as DLPOutboundMessageType } from "@whatslly/types/policy/enums";
import { DLPSettings } from "@whatslly/types/dlp/DLPSettings";
import { UserType } from "@whatslly/types/User";
import { AnalyzeRuleResponse, ExternalAnalyzeResponse, BulkAnalyzeRulesResponse, MatchInfo } from "@whatslly/types/integrations/dlp";
import { IncidentAction } from "@whatslly/types/incident/IncidentAction";
import { BgCommands } from "./bgCommands";
import { WebScriptCommands } from "./webScriptCommands";
import { webScriptClient } from "@whatslly/instant-messaging/webScriptClient";
import { ChunkedMessage } from "./messagingClient";
import { FormDataEncoder } from "form-data-encoder";
import orderBy from "lodash/orderBy";
import { IncidentsChannel } from "@whatslly/types/incident/IncidentsChannels";

const mimeTypeCache = new Map<string, RegExp>();

const addRuleIdToMatches = (matches: MatchInfo[] | undefined, ruleId: string) => {
  return matches?.map((match) => ({ ...match, ruleId: match.ruleId ?? ruleId }));
};

export type ResolveDLPParams = {
  direction?: DLPDirection;
  user: Partial<Pick<UserType, "userId" | "phone" | "name" | "managerId" | "groupIds">> | undefined;
  channel: IncidentsChannel;
  mimeType?: string;
  isTemplate?: boolean;
  isDevTools?: boolean;
  content?: File | string | { uri: string; type: string; name: string; size: number };
  file?: File;
  matchedExpressions?: string[];
  matches?: MatchInfo[];
  contactPhone?: string;
  isGroup?: boolean;
  msgType?: DLPOutboundMessageType;
};

export const DELEGATE_TO_ASYNC_PHASE = "DELEGATE_TO_ASYNC_PHASE" as const;
export type DLPResolution = { resolution: DLPAction; policy?: DLPPolicy; isCorruptedOrEncryptedFile?: boolean; isAudioTranscriptionFailed?: boolean };
export type DelegatedDLPResolution = { resolution: typeof DELEGATE_TO_ASYNC_PHASE } | DLPResolution;

export type DLPExecutorOptions = Omit<typeof defaultOptions, "analyzeRulesBulk"> & {
  analyzeRulesBulk?: typeof defaultOptions.analyzeRulesBulk;
  onShouldRefreshPolicies?: () => void;
};

async function* generateRequest(
  { content, method, urlSuffix }: { content: ResolveDLPParams["content"]; urlSuffix: string; method: "POST" },
  formData = new FormData()
) {
  if (content) {
    formData.append("content", content as any);
  }
  const encoder = new FormDataEncoder(formData);
  // const decoder = new TextDecoder();
  yield { method: method, urlSuffix: urlSuffix, headers: { ...encoder.headers } };
  for await (const chunk of encoder) {
    await new Promise((resolve) => setTimeout(resolve, 50)); // avoid blocking the event loop
    yield { body: { addToBuffer: true, data: [...chunk] } };
  }
}

export const shouldAttachMsgFile = (params: ResolveDLPParams) => {
  return Boolean(((params.content && !(params.content instanceof File)) || !params.content) && params.file && params.file instanceof File);
};

type FindPolicyResult = {
  policy?: DLPPolicy;
  isCorruptedOrEncryptedFile?: boolean;
  isAudioTranscriptionFailed?: boolean;
  matchedExpressions?: string[];
  matches?: MatchInfo[];
};

const getSource = (): "desktop" | "extension" => {
  if (typeof process !== "undefined") {
    return "desktop";
  }
  return "extension";
};

export const mapPolicyActionToActionTaken = (policyAction: DLPAction | undefined): IncidentAction =>
  policyAction === DLPAction.BLOCK ? IncidentAction.BLOCKED : IncidentAction.SENT;

export const defaultOptions = {
  async analyzeRule(rule: DLPPolicyCondition & { type: "rule" }, params: ResolveDLPParams, policyId: string) {
    const formData = new FormData();
    formData.append("policyId", JSON.stringify(policyId));
    const req = generateRequest({ urlSuffix: `/integrations/dlp/rule/${rule.id}/analyze`, method: "POST", content: params.content }, formData);
    const res = await backgroundClient.sendChunked<typeof BgCommands.HTTP_REQUEST, AnalyzeRuleResponse>(
      new ChunkedMessage(BgCommands.HTTP_REQUEST, req)
    );
    return {
      match: res.success && res.matchesRule,
      isCorruptedOrEncryptedFile: Boolean(res.success && res.isCorruptedOrEncryptedFile),
      isAudioTranscriptionFailed: Boolean(res.success && res.isAudioTranscriptionFailed),
      matchedExpressions: res.success ? res.matchedExpressions : undefined,
      matches: res.success ? res.matches : undefined,
      shouldRefreshPolicies: res.success ? res.shouldRefreshPolicies : undefined,
    };
  },
  async analyzeRulesBulk(ruleIds: string[], params: ResolveDLPParams, policyIds: string[]) {
    const formData = new FormData();
    formData.append("ruleIds", JSON.stringify(ruleIds));
    formData.append("policyIds", JSON.stringify(policyIds));
    const req = generateRequest({ urlSuffix: `/integrations/dlp/rules/analyze`, method: "POST", content: params.content }, formData);
    return await backgroundClient.sendChunked<typeof BgCommands.HTTP_REQUEST, BulkAnalyzeRulesResponse>(
      new ChunkedMessage(BgCommands.HTTP_REQUEST, req)
    );
  },
  async externalAnalyze(params: ResolveDLPParams) {
    const [selectedContact, me] = await Promise.all([
      webScriptClient.send(WebScriptCommands.GET_SELECTED_CONTACT),
      webScriptClient.send(WebScriptCommands.GET_ME),
    ]);

    const formData = new FormData();
    formData.append("sender", JSON.stringify(params.user?.name ?? "-"));
    formData.append("senderPhone", JSON.stringify(me?.phone ?? params.user?.phone ?? "-"));
    formData.append("recipient", JSON.stringify(selectedContact?.name ?? ""));
    formData.append("recipientPhone", JSON.stringify(selectedContact?.phone ?? ""));
    formData.append("assignee", JSON.stringify(params.user?.managerId ?? ""));
    formData.append("isGroup", JSON.stringify(selectedContact?.isGroup ?? "false"));
    const req = generateRequest({ urlSuffix: `/integrations/dlp/external/analyze`, method: "POST", content: params.content }, formData);
    const res = await backgroundClient.sendChunked<typeof BgCommands.HTTP_REQUEST, ExternalAnalyzeResponse>(
      new ChunkedMessage(BgCommands.HTTP_REQUEST, req)
    );
    return {
      match: res.success && res.matchesRule,
      isCorruptedOrEncryptedFile: Boolean(res.success && res.isCorruptedOrEncryptedFile),
      isAudioTranscriptionFailed: Boolean(res.success && res.isAudioTranscriptionFailed),
      matchedExpressions: res.success ? res.matchedExpressions : undefined,
      policy: res.success ? res.policy : undefined,
      matches: undefined,
    };
  },
  async notify(policy: DLPPolicy, params: ResolveDLPParams, isCorruptedOrEncryptedFile: boolean) {
    const [selectedContact, me] = await Promise.all([
      webScriptClient.send(WebScriptCommands.GET_SELECTED_CONTACT),
      webScriptClient.send(WebScriptCommands.GET_ME),
    ]);
    const formData = new FormData();
    formData.append("policyId", JSON.stringify(policy.id ?? undefined));
    formData.append("sender", JSON.stringify(params.user?.name ?? "-"));
    formData.append("senderPhone", JSON.stringify(me?.phone ?? params.user?.phone ?? "-"));
    formData.append("recipient", JSON.stringify(selectedContact?.name ?? ""));
    formData.append("recipientPhone", JSON.stringify(selectedContact?.phone ?? ""));
    formData.append("assignee", JSON.stringify(params.user?.managerId ?? ""));
    formData.append("isCorruptedOrEncryptedFile", JSON.stringify(isCorruptedOrEncryptedFile ?? "false"));
    formData.append("isGroup", JSON.stringify(selectedContact?.isGroup ?? "false"));
    formData.append("source", JSON.stringify(getSource()));
    formData.append("channel", JSON.stringify(params.channel));
    formData.append("messageTimestamp", JSON.stringify(Date.now()));
    if (params.matchedExpressions) formData.append("matchedExpressions", JSON.stringify(params.matchedExpressions));
    if (params.matches) formData.append("matches", JSON.stringify(params.matches));
    if (shouldAttachMsgFile(params)) formData.append("file", params.file as File);
    const req = generateRequest({ urlSuffix: "/integrations/dlp/notify", method: "POST", content: params.content }, formData);
    await backgroundClient.sendChunked(new ChunkedMessage(BgCommands.HTTP_REQUEST, req));
  },
  getContentMimeType: async (params: ResolveDLPParams) => {
    const { content } = params;
    return content instanceof File ? content.type : undefined;
  },
  getContentSize(params: ResolveDLPParams) {
    const { content } = params;
    return content instanceof File ? content.size : undefined;
  },
};

export class DLPExecutor {
  #sortedPolicies: DLPPolicy[];
  #allowDlpAI: boolean;
  #hasDlpDetector: boolean;
  #refreshTriggered = false;
  constructor(
    private settings: DLPSettings,
    private readonly options: DLPExecutorOptions = defaultOptions
  ) {
    this.#sortedPolicies = this.#sortedPolicies = orderBy(
      settings.policies.filter((policy) => policy.enabled),
      "priority",
      "desc"
    );
    this.#allowDlpAI = settings.allow_dlp_ai;
    this.#hasDlpDetector = settings.hasDlpDetector;
  }

  get hasPolicies() {
    return this.#sortedPolicies.length > 0 || this.#hasDlpDetector;
  }

  get hasTemplateCondition() {
    return this.#sortedPolicies.some(({ conditions }) => conditions.some((condition) => condition.type === "template"));
  }

  private getRuleIds(condition: Extract<DLPPolicyCondition, { type: "rule" }>): string[] {
    return Array.isArray(condition.id) ? condition.id : [condition.id];
  }

  private testMimeType(condition: DLPPolicyCondition & { type: "mimeType" }, mimeType: string | undefined) {
    return condition.mimeType.some((type) => {
      let regex = mimeTypeCache.get(type);
      if (!regex) {
        regex = new RegExp(type);
        mimeTypeCache.set(type, regex);
      }
      return regex.test(mimeType ?? "");
    });
  }

  private testOperator(operator: DLPOperator, value?: number, conditionValue?: number): boolean {
    if (value === undefined || conditionValue === undefined) return false;
    switch (operator) {
      case DLPOperator.GT:
        return value > conditionValue;
      case DLPOperator.LT:
        return value < conditionValue;
      case DLPOperator.GTE:
        return value >= conditionValue;
      case DLPOperator.LTE:
        return value <= conditionValue;
    }

    return false;
  }

  private testConditionSync(condition: DLPPolicyCondition, params: ResolveDLPParams): boolean {
    const { direction, user, isTemplate, isDevTools } = params;
    switch (condition.type) {
      case "direction":
        // The OUTBOUND axis is two-dimensional: the direction itself AND the msgType whitelist
        // (default [TEXT]). When the caller declares params.msgType, apply the whitelist.
        // When it doesn't, fall back to direction-only match. The fall-back preserves legacy
        // behavior for older clients still in production whose callers don't pass msgType —
        // they over-match, but they don't silently miss anything. Every new caller in this
        // repo MUST pass msgType so the whitelist gets honored; see callers in
        // blockMessaging.ts, platformDlpService.ts, MobileTuvisChatsStore.ts,
        // content-interception.ts, and reporters/tuvisDLP.ts.
        if (condition.direction === DLPDirection.OUTBOUND && params.msgType) {
          return (condition.msgType ?? [DLPOutboundMessageType.TEXT]).includes(params.msgType);
        }
        return condition.direction === direction;
      case "user":
        return condition.userId.includes(user?.userId ?? "");
      case "group":
        return condition.groupId.some((groupId) => user?.groupIds?.includes(groupId));
      case "internal_users": {
        const isOutgoingGroupMessage = params.isGroup && (direction === DLPDirection.UPLOAD || direction === DLPDirection.OUTBOUND);
        if (isOutgoingGroupMessage || !params.contactPhone || !this.settings.internalPhones?.length) return false;
        const isInternal = this.settings.internalPhones.includes(params.contactPhone);
        return condition.include ? isInternal : !isInternal;
      }
      case "fileSize":
        return this.testOperator(condition.operator ?? DLPOperator.GT, this.options.getContentSize(params), condition.size ?? condition.maxSize);
      case "template":
        return Boolean(isTemplate);
      case "devtools":
        return Boolean(isDevTools);
    }
    return false;
  }

  #triggerRefresh() {
    if (this.#refreshTriggered) return;
    this.#refreshTriggered = true;
    this.options.onShouldRefreshPolicies?.();
  }

  private async testCondition(
    condition: DLPPolicyCondition,
    params: ResolveDLPParams,
    policyId: string
  ): Promise<{
    match: boolean;
    isCorruptedOrEncryptedFile?: boolean;
    isAudioTranscriptionFailed?: boolean;
    matchedExpressions?: string[];
    matches?: MatchInfo[];
  }> {
    if (condition.type === "rule") {
      const ids = this.getRuleIds(condition);
      if (ids.length === 1) {
        const result = await this.options.analyzeRule({ ...condition, id: ids[0] }, params, policyId);
        if (result.shouldRefreshPolicies) this.#triggerRefresh();
        return { ...result, matches: addRuleIdToMatches(result.matches, ids[0]) };
      }
      const results = await Promise.all(ids.map((id) => this.options.analyzeRule({ ...condition, id }, params, policyId)));
      if (results.some((r) => r.shouldRefreshPolicies)) this.#triggerRefresh();
      const matched = results.map((result, index) => ({ ...result, ruleId: ids[index] })).filter((result) => result.match);
      return {
        match: matched.length > 0,
        isCorruptedOrEncryptedFile: matched.some((r) => r.isCorruptedOrEncryptedFile),
        isAudioTranscriptionFailed: matched.some((r) => r.isAudioTranscriptionFailed),
        matchedExpressions: matched.flatMap((r) => r.matchedExpressions ?? []),
        matches: matched.flatMap((r) => addRuleIdToMatches(r.matches, r.ruleId) ?? []),
      };
    }
    if (condition.type === "mimeType") {
      const mimeType = await this.options.getContentMimeType(params);
      return { match: this.testMimeType(condition, mimeType) };
    }
    const match = this.testConditionSync(condition, params);
    return { match };
  }

  private delegateToAsyncPhase() {
    if (!this.#allowDlpAI || this.#hasDlpDetector) return false;
    return this.#sortedPolicies.some(
      ({ conditions }) =>
        conditions.some((condition) => condition.type === "rule") &&
        // delegate only happens when uploading files. if the policy has a condition limiting it to download, it wont apply in the async phase
        !conditions.some((condition) => condition.type === "direction" && condition.direction === DLPDirection.DOWNLOAD)
    );
  }

  private findPolicySync(params: Omit<ResolveDLPParams, "content">) {
    return this.#sortedPolicies.find(({ conditions }) => conditions.every((condition) => this.testConditionSync(condition, params)));
  }

  userHasRelatedPolicy(user?: UserType) {
    if (!user) return true;
    if (this.#sortedPolicies.length === 0) return false;

    const hasPoliciesWithNoGroupIdOrUserId = this.#sortedPolicies.some((policy) => {
      const conditionsTypes = policy.conditions.map((condition) => condition.type);
      return !conditionsTypes.includes("group") && !conditionsTypes.includes("user");
    });
    if (hasPoliciesWithNoGroupIdOrUserId) return true;

    const policiesGroupsIds = this.#sortedPolicies.flatMap(
      (policy) => policy.conditions.find((condition) => condition.type === "group")?.groupId || []
    );
    const policiesUserIds = this.#sortedPolicies.flatMap((policy) => policy.conditions.find((condition) => condition.type === "user")?.userId || []);
    const userInGroupWithPolicy = user?.groupIds?.some((groupId) => policiesGroupsIds.includes(groupId));
    const userIdIsInPolicy = policiesUserIds.includes(user._id);

    return userInGroupWithPolicy || userIdIsInPolicy;
  }

  private async findMatchingPolicy(params: ResolveDLPParams) {
    if (this.#hasDlpDetector) return this.options.externalAnalyze(params);
    if ("analyzeRulesBulk" in this.options) return this.findPolicyBulk(params);
    return this.findPolicy(params);
  }

  private async findPolicy(params: ResolveDLPParams): Promise<FindPolicyResult> {
    for (const policy of this.#sortedPolicies) {
      let result = true;
      let isCorruptedOrEncryptedFileResult = false;
      let isAudioTranscriptionFailedResult = false;
      let policyMatchedExpressions: string[] = [];
      const policyMatches: MatchInfo[] = [];
      for (const condition of policy.conditions) {
        const { match, isCorruptedOrEncryptedFile, isAudioTranscriptionFailed, matchedExpressions, matches } = await this.testCondition(
          condition,
          params,
          policy.id
        );
        isCorruptedOrEncryptedFileResult = isCorruptedOrEncryptedFileResult ? true : Boolean(isCorruptedOrEncryptedFile);
        isAudioTranscriptionFailedResult = isAudioTranscriptionFailedResult ? true : Boolean(isAudioTranscriptionFailed);
        if (matchedExpressions) {
          policyMatchedExpressions = [...policyMatchedExpressions, ...matchedExpressions];
        }
        if (matches) {
          policyMatches.push(...matches);
        }
        if (!match) {
          result = false;
          break;
        }
      }
      if (result) {
        return {
          policy,
          isCorruptedOrEncryptedFile: isCorruptedOrEncryptedFileResult,
          isAudioTranscriptionFailed: isAudioTranscriptionFailedResult,
          matchedExpressions: policyMatchedExpressions ? [...new Set(policyMatchedExpressions)] : undefined,
          matches: policyMatches,
        };
      }
    }
    return {};
  }

  private async findPolicyBulk(params: ResolveDLPParams): Promise<FindPolicyResult> {
    const eligiblePolicies = new Map<string, string[][]>();
    const localMatchPolices = new Map<string, boolean>();
    for (const policy of this.#sortedPolicies) {
      const localConditions = policy.conditions.filter((c) => c.type !== "rule" && c.type !== "mimeType");
      const rules = policy.conditions.filter((c): c is Extract<DLPPolicyCondition, { type: "rule" }> => c.type === "rule");
      const mimeTypes = policy.conditions.filter((c): c is Extract<DLPPolicyCondition, { type: "mimeType" }> => c.type === "mimeType");

      const localMatch = localConditions.every((c) => this.testConditionSync(c, params));
      localMatchPolices.set(policy.id, localMatch);

      if (mimeTypes.length > 0) {
        const mimeType = await this.options.getContentMimeType(params);
        const mimeTypesMatch = mimeTypes.every((condition) => this.testMimeType(condition, mimeType));
        localMatchPolices.set(policy.id, localMatch && mimeTypesMatch);
      }

      if (rules.length > 0) {
        eligiblePolicies.set(
          policy.id,
          rules.map((r) => this.getRuleIds(r))
        );
      }
    }
    const ruleResultsMap = new Map<
      string,
      {
        matchesRule: boolean;
        isCorruptedOrEncryptedFile?: boolean;
        isAudioTranscriptionFailed?: boolean;
        matchedExpressions?: string[];
        matches?: MatchInfo[];
      }
    >();
    if (eligiblePolicies.size > 0) {
      const uniqueRuleIds = [...new Set([...eligiblePolicies.values()].flatMap((groups) => groups.flat()))];
      const policyIds = [...eligiblePolicies.keys()];
      try {
        const res = await this.options.analyzeRulesBulk!(uniqueRuleIds, params, policyIds);
        if (res && "success" in res && res.success) {
          if (res.shouldRefreshPolicies) this.#triggerRefresh();
          for (const r of res.results) {
            ruleResultsMap.set(r.ruleId, {
              matchesRule: r.matchesRule,
              isCorruptedOrEncryptedFile: r.isCorruptedOrEncryptedFile,
              isAudioTranscriptionFailed: r.isAudioTranscriptionFailed,
              matchedExpressions: r.matchedExpressions,
              matches: addRuleIdToMatches(r.matches, r.ruleId),
            });
          }
        } else {
          throw new Error("Failed to analyze rules in bulk");
        }
      } catch {
        return await this.findPolicy(params);
      }
    }

    for (const policy of this.#sortedPolicies) {
      let allRulesMatch = true;
      let isCorruptedOrEncryptedFileResult = false;
      let isAudioTranscriptionFailedResult = false;
      const policyMatchedExpressions: string[] = [];
      const policyMatches: MatchInfo[] = [];
      const ruleGroups = eligiblePolicies.get(policy.id) ?? [];
      // AND between groups, OR within each group
      for (const ruleGroup of ruleGroups) {
        let hasMatchingRuleInGroup = false;
        for (const ruleId of ruleGroup) {
          const rr = ruleResultsMap.get(ruleId);
          if (!rr?.matchesRule) {
            continue;
          }
          hasMatchingRuleInGroup = true;
          isCorruptedOrEncryptedFileResult = isCorruptedOrEncryptedFileResult || Boolean(rr.isCorruptedOrEncryptedFile);
          isAudioTranscriptionFailedResult = isAudioTranscriptionFailedResult || Boolean(rr.isAudioTranscriptionFailed);
          if (rr.matchedExpressions?.length) {
            policyMatchedExpressions.push(...rr.matchedExpressions);
          }
          if (rr.matches?.length) {
            policyMatches.push(...rr.matches);
          }
        }
        if (!hasMatchingRuleInGroup) {
          allRulesMatch = false;
          break;
        }
      }

      if (allRulesMatch && localMatchPolices.get(policy.id) === true) {
        return {
          policy,
          isCorruptedOrEncryptedFile: isCorruptedOrEncryptedFileResult,
          isAudioTranscriptionFailed: isAudioTranscriptionFailedResult,
          matchedExpressions: policyMatchedExpressions.length ? [...new Set(policyMatchedExpressions)] : [],
          matches: policyMatches,
        };
      }
    }

    return {};
  }

  resolveUploadSync(params: Omit<ResolveDLPParams, "content"> & { direction: DLPDirection.UPLOAD }, silent = false): DelegatedDLPResolution {
    if (params.direction !== DLPDirection.UPLOAD) throw new Error("Invalid direction");
    if (this.delegateToAsyncPhase()) {
      return { resolution: DELEGATE_TO_ASYNC_PHASE };
    }
    const policy = this.findPolicySync(params);
    if (!silent) this.#notifyDLP(policy, params);
    return { resolution: policy?.action ?? DLPAction.ALLOW, policy };
  }

  async resolveIsDevTools(user: Partial<Pick<UserType, "userId" | "phone" | "name" | "managerId" | "groupIds">> | undefined, silent = false) {
    const params: ResolveDLPParams = { user, isDevTools: true, channel: IncidentsChannel.WHATSAPP_WEB };
    const policy = this.findPolicySync(params);
    if (!silent) this.#notifyDLP(policy, params);
    return { resolution: policy?.action ?? DLPAction.ALLOW, policy };
  }

  async resolve(params: ResolveDLPParams, silent = false): Promise<DLPResolution> {
    this.#refreshTriggered = false;
    const { policy, isCorruptedOrEncryptedFile, isAudioTranscriptionFailed, matchedExpressions, matches } = await this.findMatchingPolicy(params);
    if (matchedExpressions) {
      params.matchedExpressions = matchedExpressions;
    }
    if (matches) {
      params.matches = matches;
    }
    if (!silent) this.#notifyDLP(policy, params, isCorruptedOrEncryptedFile);
    return { resolution: policy?.action ?? DLPAction.ALLOW, policy, isCorruptedOrEncryptedFile, isAudioTranscriptionFailed };
  }

  #notifyDLP(policy: DLPPolicy | undefined, params: ResolveDLPParams, isCorruptedOrEncryptedFile?: boolean) {
    if (!policy) {
      return;
    }
    this.options.notify(policy, params, Boolean(isCorruptedOrEncryptedFile)).catch(() => null);
  }
}
